/* tslint:disable:max-classes-per-file */
import 'proxy-polyfill/proxy.min.js';
import {BaseAPI, Configuration} from '@eurogast/hub-client-typescript';
import {ConfigService} from '@/services/ConfigService';
import {Mutations} from '@/store/types';
import Store from '@/store';
import {i18n} from '@/main.ts';

class ApiProxy<A extends BaseAPI> implements ProxyHandler<A> {

  private static getToken(propertyValue, receiver, args, resolve, reject) {
    fetch('/my/token', {
      credentials: 'include'
    }).then((resp) => resp.text())
      .then((token) => {
        ConfigService.setAccessToken(token);
        args[args.length - 1].headers.Authorization = `Bearer ${token}`;
        propertyValue.apply(receiver, args)
          .then(resolve)
          .catch(reject);
      })
      .catch((err) => {
        // refresh token or session expired
        window.location.href = '/auth/login';
      });
  }

  public get(target: A, propertyName: string, receiver: ApiProxy<A>): any {
    const propertyValue = target[propertyName];
    if (!(propertyValue instanceof Function) || propertyName === 'fetch') {
      return propertyValue;
    }
    return (...args: any[]) => {
      const callArgs = args;
      const options = {
        headers: {} as any,
        credentials: 'include'
      };
      const lastArg = args.length <= 0 ? false : args[args.length - 1];
      if (lastArg && Object.prototype.hasOwnProperty.call(lastArg, 'extendOptions')) {
        Object.assign(options, lastArg);
        callArgs[args.length - 1] = options;
      } else {
        callArgs.push(options);
      }
      if (ConfigService.hasAccessToken()) {
        options.headers.Authorization = `Bearer ${ConfigService.getAccessToken()}`;
      }
      if (ConfigService.hasLocale()) {
        options.headers['Accept-Language'] = ConfigService.getCurrentLocale();
      }
      return new Promise((resolve, reject) => {
        propertyValue.apply(receiver, args)
          .then(resolve)
          .catch((error) => {
            if (error.status === 401) {
              ApiProxy.getToken(propertyValue, receiver, args, resolve, reject);
            } else {
              reject(error);
            }
          });
      });
    };
  }
}

export abstract class ApiService {
  public static wrap<A extends BaseAPI>(TargetType: new (config?: Configuration) => A): A {
    const config = new Configuration({
      basePath: ConfigService.getHubBaseUrl()
    });

    return this.wrapInstance(new TargetType(config));
  }

  public static async defaultInfoHandler(infoMessage: string) {
    Store.commit({
      type: Mutations.Message.pushMessage,
      error: i18n.t('common.error.infoShort'),
      title: i18n.t('common.error.information'),
      message: infoMessage,
      humanMessage: infoMessage,
      connectionIssue: false
    });
  }

  public static async defaultErrorHandler(response: Response | Error): Promise<Response | Error> {
    let error: any = {};
    if (response instanceof TypeError && response.message === 'Failed to fetch') {
      error = {
        error: i18n.t('common.error.networkIssues', {errorName: response.name}),
        title: i18n.t('common.error.error'),
        message: response.message,
        humanMessage: i18n.t('common.error.whileCommunicatingWithServer', {errorName: response.name}),
        connectionIssue: true
      };
    } else if (response instanceof Error) {
      error = {
        error: i18n.t('common.error.whileCommunicatingWithServer', {errorName: response.name}),
        title: i18n.t('common.error.error'),
        message: response.message,
        connectionIssue: true
      };
    } else {
      const text = await response.text();
      try {
        error = JSON.parse(text);
      } catch (e) {
        // Response is in network log anyways, no need to print it
      }
    }
    Store.commit({
      type: Mutations.Message.pushMessage,
      error: error.error || i18n.t('common.error.unknown'),
      title: i18n.t('common.error.error'),
      message: error.message || i18n.t('common.error.noAdditionalInfo'),
      humanMessage: error.humanMessage,
      connectionIssue: error.connectionIssue
    });
    return response;
  }

  private static wrapInstance<A extends BaseAPI>(target: A): A {
    return new Proxy(target, new ApiProxy());
  }
}
